import React from 'react'
import styled, { keyframes } from 'styled-components'
import { Quote } from './MessageQuote'
import { code, quote, url } from '../utils/Regex'
import { textToHtml } from '../utils/Helpers'

type TextType = 'text' | 'link' | 'code' | 'quote' | 'deleted' | 'br';

const blockMask = '%{blck#$}'

const blinkAnimation = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`
const Root = styled.pre`
  display: flex;
  flex-direction: column;
  align-self: flex-end;
  margin: 0;
  word-wrap: break-word;
  word-break: break-word;
  background-color: ${props => props.theme.color.main.messagePartner};

  padding: 15px;
  border-radius: ${props => props.theme.borderRadius.chatMessage.regular};
  &.blinking {
    animation: ${blinkAnimation} 1.1s infinite;
  }
  &[data-full-width="true"] {
    width: calc(100% - 50px);
  }
  &[data-mine="true"] {
    background-color: ${props => props.theme.color.main.messageMy};
    color: ${props => props.theme.color.main.messageText};
  }
  &[data-mine="false"] {
    color: ${props => props.theme.color.main.messagePartnerText};
  }
  &[data-mine="false"] div[data-type="link"] {
    background-color: ${props => props.theme.color.main.linkMessagePartner.bg};
    color: ${props => props.theme.color.main.linkMessagePartner.text};
  }
`
const TextBaseDiv = styled.div`
  &:first-child {
    margin-top: 0;
  }
  &:last-child {
    margin-bottom: 0;
  }
`
const TextItemLinkDiv = styled(TextBaseDiv)`
  background-color: ${props => props.theme.color.main.linkMessage.bg};
  color: ${props => props.theme.color.main.linkMessage.text};
  cursor: pointer;
  padding: 8px;
  border-radius: 3px;
  margin: ${props => props.theme.padding.chatMessage};
  box-sizing: border-box;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
  border: 1px dashed #2196f3;
  transition: background-color 0.2s;
  word-break: break-all;
  white-space: pre-wrap;
  &:hover {
    text-decoration: underline;
    background-color: ${props => props.theme.color.main.linkMessage.hoverBg};
  }
`
const TextItemVideo = styled.video`
  width: 100%;
  max-width: 434px;
  &:first-child {
    margin-top: 0;
  }
  &:last-child {
    margin-bottom: 0;
  }
  margin: ${props => props.theme.padding.chatMessage};
`
export const CodeDiv = styled(TextBaseDiv)`
  background-color: #0f2a38;
  max-height: 60vh;
  white-space: pre;
  padding: 10px;
  font-family: monospace;
  font-size: 13px;
  color: #3bffd1;
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
`
const CodeLineDiv = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1px;
  &::before {
    content: attr(data-line);
    display: block;
    width: 25px;
    min-width: 25px;
    background-color: rgba(0, 0, 0, 0.2);
    border-right: 1px solid rgba(0, 0, 0, 0.6);
    margin-right: 8px;
    padding: 3px;
    color: rgba(255, 255, 255, 0.5);
    text-align: center;
  }
`
const TextItemTextDiv = styled(TextBaseDiv)`
  white-space: break-spaces;
`

interface FormatterProps {
  data: {
    content: string
    type: TextType
  }
}

const Formatter = (params: FormatterProps) => {
  const { content, type } = params.data

  if (type === 'link') {
    const a = document.createElement('a')
    a.href = content
    a.target = '_blank'
    a.style.display = 'absolute'
    const onLinkClick = () => {
      document.body.appendChild(a)
      a.click()
      a.remove()
    }
    if (content.toLowerCase().endsWith('.mp4')) {
      return (
        <>
          <TextItemVideo controls src={content}>
            <source src={content} type='video/mp4'></source>
          </TextItemVideo>
          <TextItemLinkDiv data-type='link' onClick={onLinkClick}>
            {content}
          </TextItemLinkDiv>
        </>
      )
    }

    return (
      <TextItemLinkDiv data-type='link' onClick={onLinkClick}>
        {content}
      </TextItemLinkDiv>
    )
  }
  else if (type === 'code') {
    const linesArr = content.split('\n')
    const lines = linesArr.map((val: string, idx: number) => {
      const lineNumber = idx + 1
      return (
        <CodeLineDiv data-line={lineNumber} key={lineNumber}>
          {val}
        </CodeLineDiv>
      )
    })
    return <CodeDiv data-type='code'>{lines}</CodeDiv>
  }
  else if (type === 'quote') {
    return <Quote data={content} />
  }
  else if (type === 'deleted') {
    return <TextItemTextDiv style={{ fontStyle: 'italic' }}>{content}</TextItemTextDiv>
  }
  return <TextItemTextDiv dangerouslySetInnerHTML={{ __html: content }} />
}

interface Props {
  msg: IMessage
  isBottomPanel?: boolean
}

export default function MessageFormatter({ msg }: Props) {
  const blocks: { type: TextType, content: string }[] = []

  const  { mine, num } = msg
  let { text } = msg
  let needFullWidth = false

  text = text.replace(new RegExp(`${url}|${code}|${quote}`, 'gims'), (a) => {
    if (a.startsWith('{code}')) {
      blocks.push({
        type: 'code',
        content: a.substr(6, a.length - 12)
      })
      needFullWidth = true
      return blockMask
    }
    if (a.startsWith('{quote type="quote-type"')) {
      blocks.push({
        type: 'quote',
        content: text,
      })
      return blockMask
    }

    blocks.push({ type: 'link', content: a })
    return blockMask
  })

  text = textToHtml(text)

  const textBlocks = text.split(blockMask)
  const textBlocksArr: { type: TextType, content: string }[] = []

  for (const i in textBlocks) {
    const simpleText = textBlocks[i]
    const specificText = blocks[i]

    if (simpleText.length > 0) textBlocksArr.push({ type: 'text', content: simpleText })
    if (specificText) textBlocksArr.push(specificText)
  }

  if (msg.status === 'deleted') {
    textBlocksArr.push({ type: 'deleted', content: 'Deleted message' })
  }

  const output = textBlocksArr.map((val, idx) => <Formatter data={val} key={idx} />)

  return (
    <Root data-id={num ? num : msg.created} data-full-width={needFullWidth} data-mine={mine} data-type="message">
      {output}
    </Root>
  )
}